import { css, SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';
import { EmotionTheme } from 'src/domains/layouts/config/emotionTheme';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';
import { LiveTvIcon } from 'src/domains/layouts/shared/assets/icons/LiveTvIcon';
import { LiveTvSecondaryIcon } from 'src/domains/layouts/shared/assets/icons/LiveTvSecondaryIcon';
import { withConfig } from 'src/withConfig';

interface TableSportsRowHrEventPropsType {
    variant: DefaultComponentVariantType | null;
}

const setTableSportsRowHrEventBox = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            padding: ${theme.star.padding.small} ${theme.star.padding.small} 0;
            border-radius: ${theme.star.radius.medium};
            overflow: hidden;
            @media ${theme.star.mediaQuery.tablet} {
                padding: ${theme.star.padding.small};
            }
        `;
    }
    return css`
        @media ${theme.star.mediaQuery.tablet} {
            &::after {
                background: ${theme.star.meetingsRowColors.borderColor};
                content: '';
                height: 1px;
                margin-top: -1px;
                position: absolute;
                top: 100%;
                width: 100%;
            }
        }
    `;
};

export const TableSportsRowHrEventBox = withConfig(theme => styled('div', { label: 'TableSportsRowHrEventBox' })<TableSportsRowHrEventPropsType>`
    ${({ variant }): SerializedStyles=> setTableSportsRowHrEventBox(variant, theme)};
    background-color: ${theme.star.meetingsRowColors.bgColor};
    align-items: start;
    display: flex;
    flex-flow: column nowrap;
    justify-content: center;
    @media ${theme.star.mediaQuery.tablet} {
        align-items: center;
        flex-direction: row;
        justify-content: start;
        position: relative;
    }
`);

const setEventBoxHeaderWrapper = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            border-radius: ${theme.star.radius.small};
            margin-bottom: 4px;
            @media ${theme.star.mediaQuery.tablet} {
                margin-bottom: 0;
            }
        `;
    }
    return css`
        border-color: ${theme.star.meetingsRowColors.borderColor};
        border-style: solid;
        border-width: 1px;
        @media ${theme.star.mediaQuery.tablet} {
            border-width: 0 1px;
        }
    `;
};

export const EventBoxHeaderWrapper = withConfig(theme => styled('header', { label: 'EventBoxHeaderWrapper' })<TableSportsRowHrEventPropsType>`
    ${({ variant }): SerializedStyles=> setEventBoxHeaderWrapper(variant, theme)};
    align-items: center;
    align-self: stretch;
    background-color: ${theme.star.tableSportsRow.horseRacingEvent.bgColor};
    color: ${theme.star.tableSportsRow.horseRacingEvent.txtColorSecondary};
    display: flex;
    gap: 8px;
    height: 34px;
    line-height: ${theme.star.tableSportsRow.horseRacingEvent.lineHeightPrimary};
    justify-content: space-between;
    overflow: hidden;
    padding: 8px 12px;
    width: 100%;
    @media ${theme.star.mediaQuery.tablet} {
        height: auto;
        background: transparent;
        line-height: ${theme.star.tableSportsRow.horseRacingEvent.lineHeightSecondary};
        flex: 0 0 160px;
        padding: 8px 12px;
        color: ${theme.star.tableSportsRow.horseRacingEvent.txtColor};
    }
`);

export const EventBoxHeader = withConfig(theme => styled('h5', { label: 'EventBoxHeader' })`
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: ${theme.star.tableSportsRow.horseRacingEvent.fontSizePrimary};
    font-weight: ${theme.star.tableSportsRow.horseRacingEvent.fontWeightPrimary};
    @media ${theme.star.mediaQuery.tablet} {
        font-weight: ${theme.star.tableSportsRow.horseRacingEvent.fontWeightSecondary};
        font-size: ${theme.star.tableSportsRow.horseRacingEvent.fontSizeSecondary};
    }
`);

const setChildrenRow = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            gap: ${theme.star.gap.small};
            padding: ${theme.star.padding.small} 0;
            @media ${theme.star.mediaQuery.tablet} {
                padding: 0;
            }
        `;
    }
    return css`
        @media ${theme.star.mediaQuery.tablet} {
            gap: 1px;
        }
    `;
};

export const ChildrenRow = withConfig(theme => styled('div', { label: 'ChildrenRow' })<TableSportsRowHrEventPropsType>`
    ${({ variant }): SerializedStyles=> setChildrenRow(variant, theme)};
    width: 100%;
    display: flex;
    flex-flow: row nowrap;
    overflow: auto hidden;
    @media ${theme.star.mediaQuery.tablet} {
        flex-wrap: wrap;
    }
`);

export const StreamIco = withConfig(theme => styled(LiveTvIcon, { label: 'StreamIco' })`
    fill: ${theme.star.meetingsListColors.bgColorSenary};
    flex: 0 0 16px;
    width: 16px;
    @media ${theme.star.mediaQuery.tablet} {
        flex-wrap: wrap;
        fill: ${theme.star.meetingsListColors.bgColorQuaternary};
    }
`);

export const StreamSecondaryIco = withConfig(theme => styled(LiveTvSecondaryIcon, { label: 'StreamSecondaryIco' })`
    fill: ${theme.star.meetingsListColors.bgColorSenary};
    flex: 0 0 16px;
    width: 16px;
    @media ${theme.star.mediaQuery.tablet} {
        flex-wrap: wrap;
        fill: ${theme.star.meetingsListColors.bgColorQuaternary};
    }
`);
